import { type ComponentPropsWithoutRef, type ReactNode, useState } from 'react';
import cx from 'clsx';
import { Footer } from '@repo/domain-kit/shared';
import { ATTACHMENTS } from 'receivable-invoices-js/react/constants/invoices';
import { QontoHeader } from 'receivable-invoices-js/react/components/shared/qonto-header';
import { useMedia } from 'receivable-invoices-js/react/hooks/use-media';
import type { Quote } from 'receivable-invoices-js/react/api/models/quote';
import type { CreditNote } from 'receivable-invoices-js/react/api/models/credit-note';
import type { ReceivableInvoice } from 'receivable-invoices-js/react/api/models/receivable-invoice';
import type { PaymentLink } from 'receivable-invoices-js/react/api/models/payment-link';
import type { PaymentPollingStatus } from 'receivable-invoices-js/react/routes/invoices/index/hooks/use-status-polling';
import type { Document, Merchant, Attachment, DocumentVariant } from './types';
import { DocumentHeader } from './document-header';
import { AttachmentsSection } from './attachments-section';
import { PdfSection } from './pdf-section';
import { MainContent } from './main-content';
import { MobileActions } from './mobile-actions';
import styles from './responsive-view.module.css';

const sleep = (ms: number): Promise<void> =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

const buildStreetAddress = (line1?: string, line2?: string): string =>
  [line1, line2].filter(Boolean).join('');

interface ResponsiveViewProps extends ComponentPropsWithoutRef<'div'> {
  document: Quote | CreditNote | ReceivableInvoice;
  type?: 'invoice' | 'quote' | 'credit-note';
  attachments?: Attachment[];
  paymentLink?: PaymentLink | null;
  paymentPollingStatus?: PaymentPollingStatus;
  isClientPortalEnabled?: boolean;
}

export function ResponsiveView({
  className,
  document,
  type,
  attachments = [],
  paymentLink,
  paymentPollingStatus,
  isClientPortalEnabled,
  ...props
}: ResponsiveViewProps): ReactNode {
  const { isDesktop, isMobile } = useMedia();
  const [isAttachmentsLoaded, setIsAttachmentsLoaded] = useState({
    [ATTACHMENTS.ATTACHMENT]: false,
    [ATTACHMENTS.TERMS_CONDITIONS]: false,
  });

  const clientPortalId = document.customerSnapshot?.id;
  const documentType = type || 'invoice';

  const client = document.customerSnapshot
    ? {
        name: document.customerSnapshot.name,
        billingAddress: {
          streetAddress: buildStreetAddress(
            document.customerSnapshot.addressLine1,
            document.customerSnapshot.addressLine2
          ),
          zipCode: document.customerSnapshot.addressZipcode,
          city: document.customerSnapshot.addressCity,
          countryCode: document.customerSnapshot.addressCountry,
        },
        tinNumber: document.customerSnapshot.tinNumber,
        vatNumber: document.customerSnapshot.vatNumber,
        locale: document.locale || 'en',
      }
    : undefined;

  const merchant: Merchant = {
    legalName: document.organization.legalName || '',
    shortLegalForm: document.organization.legalForm || '',
    address: {
      streetAddress: buildStreetAddress(
        document.organization.addressLine1,
        document.organization.addressLine2
      ),
      zipCode: document.organization.addressZipcode || '',
      city: document.organization.addressCity || '',
      countryCode: document.organization.addressCountry || '',
    },
    legalCountry: (document.organization.addressCountry || 'EN') as DocumentVariant,
  };

  const hasAttachments = attachments.length > 0;

  const setAttachmentHasLoaded = (attachmentType: string) => {
    if (typeof isAttachmentsLoaded[attachmentType] !== 'undefined') {
      setIsAttachmentsLoaded(prev => ({ ...prev, [attachmentType]: true }));
    }
  };

  const downloadAll = async () => {
    const createLink = (url: string, fileName?: string) => {
      const link = window.document.createElement('a');
      link.style.display = 'none';
      link.href = url;
      link.download = fileName || 'invoice';
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
    };

    createLink(document.pdfUrl || '');
    await sleep(1000);

    for (const attachment of attachments) {
      createLink(attachment.url, attachment.filename);
      // eslint-disable-next-line no-await-in-loop -- Required for download timing
      await sleep(1000);
    }
  };

  return (
    <div
      className={cx(styles.responsiveContainer, className)}
      data-test-document-responsive-view
      data-testid="responsive-view"
      {...props}
    >
      <div className={styles.leftColumn} data-test-document-responsive-view-left-column>
        <QontoHeader className={styles.qontoHeader} role={isDesktop ? 'banner' : undefined} />
        <div className={styles.pdfContainer} role={isDesktop ? 'main' : undefined}>
          <PdfSection
            client={client}
            document={document as Document}
            documentType={documentType}
            merchant={merchant}
          />
          <AttachmentsSection
            attachments={attachments}
            isAttachmentsLoaded={isAttachmentsLoaded}
            onAttachmentLoad={setAttachmentHasLoaded}
          />
        </div>
        <div className={styles.footer}>
          <Footer hasPaymentLink={Boolean(paymentLink)} />
        </div>
      </div>

      <div className={styles.rightColumn} data-test-document-responsive-view-right-column>
        <DocumentHeader
          clientPortalId={clientPortalId}
          isClientPortalEnabled={isClientPortalEnabled}
        />
        <div role={isDesktop ? 'complementary' : 'main'}>
          <MainContent
            clientPortalId={clientPortalId}
            document={document}
            downloadAll={downloadAll}
            hasAttachments={hasAttachments}
            isClientPortalEnabled={isClientPortalEnabled}
            isMobile={isMobile}
            paymentLink={paymentLink}
            paymentPollingStatus={paymentPollingStatus}
            type={type}
          />
        </div>
        <div className={styles.footer}>
          <Footer hasPaymentLink={Boolean(paymentLink)} />
        </div>
      </div>

      <div className={styles.mobileActions} role="toolbar">
        <MobileActions
          clientPortalId={clientPortalId}
          document={document}
          downloadAll={downloadAll}
          hasAttachments={hasAttachments}
          isClientPortalEnabled={isClientPortalEnabled}
          paymentLink={paymentLink}
          type={type}
        />
      </div>
    </div>
  );
}
